import { type AxiosError, isAxiosError } from 'axios';
import type * as CSS from 'csstype';
import type { Editor } from 'grapesjs';
import { produce } from 'immer';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useAuth } from '@gbs-monorepo-packages/auth';
import {
  DefaultDescriptionBenefitEducation,
  IApiThrowsError,
  ManagerRoles,
  Roles,
  formatUnknownErrorMessage,
  getRouteFrom,
  useToast,
} from '@gbs-monorepo-packages/common';

import { FullscreenLoader } from '../../components/FullscreenLoader';
import { handlerSetDefaultRules } from '../../components/GrapesJS/Customs';
import { updateSrcVideoAttribute } from '../../components/GrapesJS/Customs/Default';
import {
  handlerWrapper,
  wrapperClass,
} from '../../components/GrapesJS/Customs/ToggleWrapper';
import { WebBuilder } from '../../components/WebBuilder/index';
import {
  CLIENT_URL,
  FREQUENCY_EDIT_TIME_UPDATE_SENT,
  GOOGLE_FONTS_API_URL,
} from '../../constants/Env';
import { type ICustomFont } from '../../constants/Fonts';
import { globalStyleDefault } from '../../constants/GlobalStyles';
import { NOT_FOUND } from '../../constants/RoutePaths';
import {
  type IEditCourseState,
  type ILocation,
} from '../../constants/RouteStates';
import {
  type ICoursePage,
  type IDeleteCoursePageInfo,
  type ITemplatePage,
} from '../../contexts/coursePage';
import { useCourse } from '../../hooks/useCourse';
import { useCoursePages } from '../../hooks/useCoursePages';
import { useDragDrop } from '../../hooks/useDragDrop';
import { useSelectedClient } from '../../hooks/useSelectedClient';
import {
  type IBatchCoursePagesToPatch,
  type ICoursePageDTO,
  type ICreateCoursePageProps,
  type IHistoryPage,
  type IPatchCoursePagesProps,
  type IUpdateCoursePageProps,
  deleteCoursePage,
  patchCoursePages,
  patchCoursePagesComponents,
  snapshotCourseVersionFromSavedState,
} from '../../services/coursePages';
import {
  type ICourseSettingsProps,
  type IFontDTO,
  updateCourseEditTime,
} from '../../services/courses';
import {
  type ITemplateDTO,
  createTemplatePage,
  updateTemplateEditTime,
  updateTemplatePage,
} from '../../services/templates';
import { generateNegativeTimestamp } from '../../utils/generate';
import Logger from '../../utils/logger';
import { ByPageNumber } from '../../utils/sortObjects';
import { CoursePages } from './components/CoursePages';
import { SettingCourseHeader } from './components/SettingCourseHeader';
import {
  Container,
  LoadingCoursePages,
  LoadingCoursePagesContainer,
  MainContainer,
  MainContent,
  NoPagesContainer,
  NoPagesText,
} from './styles';

type ICoursePageEditorContent = Pick<
  ICoursePage,
  'components' | 'cssContent' | 'hasWrapper' | 'htmlContent' | 'idExternal'
>;

type ITemplatePageEditorContent = Pick<
  ITemplatePage,
  'components' | 'cssContent' | 'hasWrapper' | 'htmlContent' | 'idExternal'
>;

const checkEditorContentChanged = (
  currentEditor: Editor,
  { cssContent, hasWrapper, components }: ICoursePageEditorContent
) => {
  const wrapperClasses: string[] =
    (currentEditor.getWrapper()?.getClasses() as string[]) ?? [];

  const hasEditorWrapper: boolean = wrapperClasses.includes(wrapperClass);
  if (hasWrapper !== hasEditorWrapper) {
    return true;
  }

  const cssEditorContent = currentEditor.getCss({ json: false }) as string;
  if (cssContent !== cssEditorContent) {
    return true;
  }

  const componentsEditorContent = JSON.stringify(
    currentEditor.getWrapper()?.toJSON()
  );

  components = JSON.stringify(JSON.parse(components ?? ''));
  if (components !== componentsEditorContent) {
    return true;
  }

  return false;
};

const checkContentChanged = (
  {
    cssContent: newCssContent,
    htmlContent: newHtmlContent,
    hasWrapper: newHasWrapper,
    components: newComponents,
  }: ICoursePageEditorContent,
  { cssContent, htmlContent, hasWrapper, components }: ICoursePageEditorContent
) => {
  return (
    hasWrapper !== newHasWrapper ||
    htmlContent !== newHtmlContent ||
    cssContent !== newCssContent ||
    components !== newComponents
  );
};

const COURSE_PAGES_BATCH_SIZE = 10;

interface IUpdateCoursePagesParams {
  courseId: number;
  coursePages: ICoursePage[];
  lastCoursePages: ICoursePageDTO[];
  updatedPages?: ICoursePage[];
}

const updateCoursePages = ({
  courseId,
  coursePages,
  lastCoursePages,
  updatedPages = [],
}: IUpdateCoursePagesParams) => {
  const pages: IBatchCoursePagesToPatch = [];

  coursePages.forEach((page, index) => {
    let templateColorsFoundAux;

    if (updatedPages.length > 0) {
      const updatedPage = updatedPages.find(
        (updatedPage) => updatedPage.id === page.id
      );

      templateColorsFoundAux = updatedPage?.templateColorsFound;
    } else {
      templateColorsFoundAux = page.templateColorsFound;
    }

    const {
      id,
      cssContent,
      edited,
      hasWrapper,
      htmlContent,
      isNew = false,
      pageNumber,
      title,
      isNewFromTemplate = false,
      components,
      templateColorsFound = templateColorsFoundAux,
    } = page;

    const position = index + 1;
    if (isNew || id < 0) {
      const pageUpdated: ICreateCoursePageProps = {
        courseId,
        cssContent,
        hasWrapper,
        htmlContent,
        pageNumber: position,
        title,
        components,
        templateColorsFound,
      };
      pages.push(pageUpdated);
      return;
    } else if (isNewFromTemplate) {
      // page already exists, but was not saved yet
      const pageUpdated: IUpdateCoursePageProps = {
        id,
        cssContent,
        hasWrapper,
        htmlContent,
        components,
        pageNumber: position,
        title,
        templateColorsFound: templateColorsFoundAux,
      };
      pages.push(pageUpdated);
      return;
    }

    if (edited ?? false) {
      const pageUpdated: IUpdateCoursePageProps = {
        id,
      };
      if (pageNumber !== position) {
        pageUpdated.pageNumber = position;
      }
      const lastIndex = lastCoursePages.findIndex((old) => old.id === id);
      if (lastIndex >= 0) {
        const oldPage = lastCoursePages[lastIndex];

        if (cssContent !== oldPage.cssContent) {
          pageUpdated.cssContent = cssContent;
        }
        if (htmlContent !== oldPage.htmlContent) {
          pageUpdated.htmlContent = htmlContent;
        }
        if (title !== oldPage.title) {
          pageUpdated.title = title;
        }
        if (hasWrapper !== oldPage.hasWrapper) {
          pageUpdated.hasWrapper = hasWrapper;
        }
        if (components !== oldPage.components) {
          pageUpdated.components = components;
        }
        if (templateColorsFound !== oldPage.templateColorsFound) {
          pageUpdated.templateColorsFound = templateColorsFound;
        }
        lastCoursePages.splice(lastIndex, 1);
      }
      pages.push(pageUpdated);
      return;
    }

    if (pageNumber !== position) {
      const pageUpdated: IUpdateCoursePageProps = {
        id,
        pageNumber: position,
      };
      pages.push(pageUpdated);
    }
  });

  return pages.length
    ? {
        courseId,
        pages,
      }
    : null;
};

const createFindById =
  (currentCoursePage: Pick<ICoursePage, 'id'>) => (page: ICoursePage) =>
    page.id === currentCoursePage.id;
const createFindByContent =
  (currentCoursePage: ICoursePage) =>
  ({ htmlContent, cssContent, title, hasWrapper, components }: ICoursePage) =>
    title === currentCoursePage.title &&
    hasWrapper === currentCoursePage.hasWrapper &&
    htmlContent === currentCoursePage.htmlContent &&
    cssContent === currentCoursePage.cssContent &&
    components === currentCoursePage.components;

export interface ICourseWebsocketMessage {
  id: number;
  type: string;
  message?: string;
  title?: string;
  section?: string;
}

export const EditCourse = (): JSX.Element => {
  const [editor, setEditor] = useState<Editor | null>(null);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const [loadingCourse, setLoadingCourse] = useState(false);
  const [loadingCoursePages, setLoadingCoursePages] = useState(false);
  const [loadingEditor, setLoadingEditor] = useState(false);

  const [needSave, setNeedSave] = useState(false);
  const [savingCourse, setSavingCourse] = useState(false);

  const {
    changeCoursePageContent,
    coursePages,
    templatePages,
    overwriteCoursePages,
    selectedCoursePageIndex,
    selectCoursePageIndex,
    syncCoursePages,
    syncTemplatePage,
    deleteMultipleCoursePages,
  } = useCoursePages();

  const { clientId = '', courseId = '', templateId = '' } = useParams();
  const {
    saveCourse,
    selectedCourse,
    selectCourseByID,
    updateCourse,
    updateTemplate,
    selectedTemplate,
    updateCourseSettings,
    saveTranslationSettings,
  } = useCourse();

  const { state }: ILocation<IEditCourseState> = useLocation();
  const { user, getCurrentRole } = useAuth();
  const { selectedClient, loadingSelectedClient } = useSelectedClient();
  const navigate = useNavigate();

  const customConfig = useMemo(() => ({ pageManager: { pages: [] } }), []);

  const lastCoursePages = useRef<ICoursePageDTO[]>([]);
  const lastTemplatePage = useRef<ITemplateDTO[]>([]);

  const { addToast } = useToast();
  const { isDragging } = useDragDrop();

  const [isAllowedToEdit, setIsAllowedToEdit] = useState(false);
  const [isUserAdmin, setIsUserAdmin] = useState(false);
  const [isLockedByOtherUser, setIsLockedByOtherUser] = useState(false);

  const [defaultFontsCount, setDefaultFontsCount] = useState(13);

  const isUserManager = useMemo(() => {
    const currentRole = getCurrentRole(user?.roles ?? []);
    return (
      (currentRole != null && ManagerRoles.includes(currentRole.key)) ?? false
    );
  }, [getCurrentRole, user]);

  const editTimeout = useRef<NodeJS.Timer | null>(null);
  const skipSetNeedSave = useRef(false);
  const updateNeedSaveTimeout = useRef<NodeJS.Timeout | null>(null);

  const allowEdit = useMemo(
    () => isAllowedToEdit && !isLockedByOtherUser,
    [isAllowedToEdit, isLockedByOtherUser]
  );
  useEffect(() => {
    if (localStorage.getItem('originalRoute')) {
      localStorage.removeItem('originalRoute');
    }
  }, []);

  const isClientFullCourseEditor = useMemo(
    () =>
      window.location.origin === CLIENT_URL &&
      (selectedClient?.hasFullCourseEditor ?? false),
    [selectedClient?.hasFullCourseEditor]
  );

  useEffect(() => {
    const contentOwnerClientId = selectedCourse?.clientId;
    const selectedClientId = selectedClient?.id ?? 0;
    if (
      contentOwnerClientId != null &&
      selectedClientId &&
      contentOwnerClientId !== selectedClientId
    ) {
      addToast({
        title: 'Content not found',
        description: 'This content does not exist in this client.',
        styleType: 'error',
        dataCy: 'error-toast',
      });
      navigate(getRouteFrom(NOT_FOUND), { replace: true });
    }
  }, [addToast, navigate, selectedClient?.id, selectedCourse?.clientId]);

  useEffect(() => {
    deleteEditTimeouts();

    if (templateId) {
      setEditTemplateTimeout();
    } else if (courseId) {
      setEditCourseTimeout();
    }

    return () => {
      deleteEditTimeouts();
    };
  }, [isLockedByOtherUser]);

  const reloadEditor = useCallback(() => {
    if (isLockedByOtherUser && !isAllowedToEdit) {
      window.location.reload();
    }
  }, [isAllowedToEdit, isLockedByOtherUser]);

  const handleUpdateCourseEditTime = useCallback(() => {
    Logger.notice('Sending a Course Edit Update message to the server');

    updateCourseEditTime({ courseId: Number(courseId) })
      .then(() => {
        if (isLockedByOtherUser) {
          addToast({
            title: 'Course available for editing',
            description:
              'Re-open the course to obtain the changes or override with yours',
            styleType: 'success',
            dataCy: 'course-is-available-editing-toast',
            duration: FREQUENCY_EDIT_TIME_UPDATE_SENT - 1000,
          });
        }

        reloadEditor();
      })
      .catch((err: AxiosError | Error) => {
        setIsLockedByOtherUser(true);
        setIsAllowedToEdit(false);
        setSavingCourse(false);

        if (isAxiosError(err) && err.response && err.response.status === 423) {
          addToast({
            title: 'Course is locked',
            description: formatUnknownErrorMessage(
              err,
              DefaultDescriptionBenefitEducation
            ),
            styleType: 'error',
            dataCy: 'course-is-locked-toast',
            duration: 3000,
          });
        } else {
          Logger.error(
            'Error while sending the Course Edit Update message: ' + err.message
          );

          addToast({
            title: 'Error while updating course',
            description: formatUnknownErrorMessage(
              err,
              DefaultDescriptionBenefitEducation
            ),
            styleType: 'error',
            dataCy: 'course-edit-time-error-toast',
          });
        }
      });
  }, [addToast, courseId, isLockedByOtherUser, reloadEditor]);

  const handleUpdateTemplateTime = useCallback(() => {
    Logger.notice('Sending a Template Edit Update message to the server');

    updateTemplateEditTime({ templateId: Number(templateId) })
      .then(() => {
        if (isLockedByOtherUser) {
          addToast({
            title: 'Template available for editing',
            description:
              'Re-open the template to obtain the changes or override with yours',
            styleType: 'success',
            dataCy: 'template-is-available-editing-toast',
            duration: FREQUENCY_EDIT_TIME_UPDATE_SENT - 1000,
          });
        }

        reloadEditor();
      })
      .catch((err: AxiosError | Error) => {
        setIsLockedByOtherUser(true);
        setIsAllowedToEdit(false);
        setSavingCourse(false);

        if (isAxiosError(err) && err.response && err.response.status === 423) {
          addToast({
            title: 'Template is locked',
            description: formatUnknownErrorMessage(
              err,
              DefaultDescriptionBenefitEducation
            ),
            styleType: 'error',
            dataCy: 'template-is-locked-toast',
            duration: 3000,
          });
        } else {
          Logger.error(
            'Error while sending the Template Edit Update message: ' +
              err.message
          );

          addToast({
            title: 'Error while updating template edit time',
            description: formatUnknownErrorMessage(
              err,
              DefaultDescriptionBenefitEducation
            ),
            styleType: 'error',
            dataCy: 'template-edit-time-error-toast',
          });
        }
      });
  }, [addToast, isLockedByOtherUser, reloadEditor, templateId]);

  const deleteEditTimeouts = useCallback(() => {
    if (editTimeout.current) {
      clearInterval(editTimeout.current);

      editTimeout.current = null;

      Logger.success('Cleared all Edit Update intervals');
    }
  }, []);

  const setEditCourseTimeout = () => {
    editTimeout.current = setInterval(
      handleUpdateCourseEditTime,
      FREQUENCY_EDIT_TIME_UPDATE_SENT
    );
  };

  const setEditTemplateTimeout = () => {
    editTimeout.current = setInterval(
      handleUpdateTemplateTime,
      FREQUENCY_EDIT_TIME_UPDATE_SENT
    );
  };

  useEffect(() => {
    const currentRole = getCurrentRole(user?.roles ?? []);
    if (currentRole?.key === Roles.ADMIN) {
      setIsUserAdmin(true);
    }
  }, [getCurrentRole, user]);

  useEffect(() => {
    const syncCourseOnMount = async () => {
      const courseIdFromPath = Number(courseId);

      if (!courseIdFromPath) {
        return;
      }
      const courseFromState = state?.course ?? null;
      setLoadingCourse(true);
      if (
        courseFromState?.globalStyle?.additionalColors &&
        courseFromState.id === courseIdFromPath &&
        Object.keys(courseFromState.globalStyle.additionalColors).length > 0
      ) {
        updateCourse(courseFromState);
      } else {
        await selectCourseByID(courseIdFromPath);
      }
      window.history.replaceState(null, '');
      setLoadingCourse(false);
      setIsAllowedToEdit(true);
    };

    void syncCourseOnMount();
  }, []);

  // For templates
  useEffect(() => {
    let mount = true;
    const syncPagesOnMount = async () => {
      if (!templateId) {
        return;
      }

      lastTemplatePage.current = [];

      setLoadingCoursePages(true);

      try {
        const result = await syncTemplatePage(Number(templateId));
        if (mount) {
          lastTemplatePage.current = result;
          updateTemplate(
            lastTemplatePage.current[lastTemplatePage.current.length - 1]
          );
          setIsAllowedToEdit(true);

          handleUpdateTemplateTime();
        }
      } catch (err) {
        addToast({
          title: 'Error on loading course pages',
          description: formatUnknownErrorMessage(
            err,
            DefaultDescriptionBenefitEducation
          ),
          styleType: 'error',
          dataCy: 'load-course-pages-error-toast',
        });
      } finally {
        setLoadingCoursePages(false);
      }
    };

    void syncPagesOnMount();

    return () => {
      mount = false;
      deleteEditTimeouts();
    };
  }, []);

  // For Courses
  useEffect(() => {
    let mount = true;
    const syncPagesOnMount = async () => {
      const courseIdFromPath = Number(courseId);

      if (!courseIdFromPath) {
        return;
      }
      lastCoursePages.current = [];

      setLoadingCoursePages(true);
      try {
        const result = await syncCoursePages(courseIdFromPath);

        if (mount) {
          lastCoursePages.current = result;
          setLoadingCoursePages(false);
          setIsAllowedToEdit(true);

          handleUpdateCourseEditTime();
        }
      } catch (err) {
        addToast({
          title: 'Error on loading course pages',
          description: formatUnknownErrorMessage(
            err,
            DefaultDescriptionBenefitEducation
          ),
          styleType: 'error',
          dataCy: 'load-course-pages-error-toast',
        });
      }
    };

    void syncPagesOnMount();

    return () => {
      mount = false;
      deleteEditTimeouts();
    };
  }, []);

  const handleSetNeedSave = useCallback((value: boolean) => {
    if (skipSetNeedSave.current) {
      return;
    }

    if (updateNeedSaveTimeout.current) {
      clearTimeout(updateNeedSaveTimeout.current);
    }

    updateNeedSaveTimeout.current = setTimeout(() => {
      setNeedSave(value);
    }, 1000);
  }, []);

  const startIgnorePeriod = useCallback((duration = 1000) => {
    skipSetNeedSave.current = true;

    setTimeout(() => {
      skipSetNeedSave.current = false;
    }, duration);
  }, []);

  const updateEditorWithContent = (
    currentEditor: Editor,
    {
      cssContent,
      htmlContent,
      idExternal,
      components,
      hasWrapper,
    }: ICoursePageEditorContent | ITemplatePageEditorContent,
    startIgnorePeriod?: () => void
  ): void => {
    if (startIgnorePeriod) {
      startIgnorePeriod();
    }

    const regex =
      /<style(?=[\s>])(?:(?!<\/style>)[\s\S])*<\/style>|style\s*=/gi;
    const matches = htmlContent.match(regex);

    if (!matches) {
      if (!idExternal) {
        currentEditor.Css.clear();
      }
    }

    currentEditor.selectRemove(currentEditor.getSelectedAll());
    currentEditor.Components.clear();
    if (components) {
      const bkpComponents = JSON.parse(components) as object;
      currentEditor.Components.load(bkpComponents);
    } else {
      currentEditor.setComponents(htmlContent, {});
    }
    handlerSetDefaultRules(currentEditor, cssContent ?? '');
    handlerWrapper(currentEditor, hasWrapper);
    updateSrcVideoAttribute(currentEditor);
    currentEditor.UndoManager.clear();
  };

  useEffect(() => {
    const handleUpdate = () => {
      if (needSave) return;
      const oldPage = templateId
        ? lastTemplatePage.current.at(-1)
        : lastCoursePages.current.at(selectedCoursePageIndex);

      if (!editor || !oldPage) {
        handleSetNeedSave(true);
      } else {
        const pageContent = oldPage as ICoursePageEditorContent;
        const needToBeSaved = checkEditorContentChanged(editor, pageContent);
        handleSetNeedSave(needToBeSaved);
      }
    };

    if (editor) {
      editor.on('update', handleUpdate);
    }

    return () => {
      editor?.off('update', handleUpdate);
      if (updateNeedSaveTimeout.current) {
        clearTimeout(updateNeedSaveTimeout.current);
      }
    };
  }, [
    coursePages,
    templatePages,
    editor,
    needSave,
    selectCoursePageIndex,
    selectedCoursePageIndex,
    handleSetNeedSave,
    courseId,
    templateId,
  ]);

  const handleSetEditor = useCallback(
    (editor: Editor) => {
      setLoadingEditor(true);
      setEditor(editor);
      if (templateId && templatePages.length) {
        updateEditorWithContent(
          editor,
          templatePages[templatePages.length - 1],
          startIgnorePeriod
        );
      } else {
        const firstIndex =
          selectedCoursePageIndex > 0 ? selectedCoursePageIndex : 0;
        const firstPage = coursePages.at(firstIndex);

        if (firstPage) {
          updateEditorWithContent(editor, firstPage, startIgnorePeriod);
          selectCoursePageIndex(firstIndex);
        }
      }
      setLoadingEditor(false);
    },
    [
      coursePages,
      selectCoursePageIndex,
      templateId,
      templatePages,
      selectedCoursePageIndex,
      startIgnorePeriod,
    ]
  );

  useEffect(() => {
    const {
      passcodeProtected = false,
      viewURL = null,
      passcode = null,
      status = 'In Progress',
    } = selectedCourse ?? {};

    const settings: ICourseSettingsProps = {
      passcodeProtected,
      viewUrl: viewURL?.trim() ?? null,
      status: status ?? 'In Progress',
    };

    if (passcodeProtected) settings.passcode = passcode?.trim();

    updateCourseSettings(settings);
  }, [selectedCourse, updateCourseSettings]);

  useEffect(() => {
    const handleStopPreview = () => {
      if (editor) {
        editor.runCommand('preview');
        editor.runCommand('previewAllowEdit', { allowEdit });
      }
    };

    if (editor) {
      if (!allowEdit) {
        editor.runCommand('preview');
        editor.runCommand('previewAllowEdit', { allowEdit });
        editor.on('stop:preview', handleStopPreview);
      } else {
        editor.runCommand('previewAllowEdit', { allowEdit });
        editor.off('stop:preview', handleStopPreview);
      }
    }

    return () => {
      editor?.off('stop:preview', handleStopPreview);
    };
  }, [editor, isAllowedToEdit, isLockedByOtherUser, allowEdit]);

  const handleChangeTitle = () => {
    setNeedSave(true);
  };

  const handleUpdateTemplate = useCallback(async () => {
    if (!editor || !templateId || !selectedTemplate) return;

    const htmlContent = editor.getHtml();
    const components = editor.getWrapper()?.toJSON() ?? '';
    const cssContent = editor.getCss({ json: false }) as string;

    const wrapperClasses: string[] =
      (editor.getWrapper()?.getClasses() as string[]) ?? [];
    const hasWrapper: boolean = wrapperClasses.includes(wrapperClass);

    try {
      deleteEditTimeouts();

      await updateTemplatePage({
        id: Number(templateId),
        clientId: selectedTemplate.clientId,
        cssContent,
        hasWrapper,
        htmlContent,
        createdAt: '',
        updatedAt: '',
        components: JSON.stringify(components),
        title: selectedTemplate?.title,
      });

      setEditTemplateTimeout();

      const result = await syncTemplatePage(Number(templateId));
      lastTemplatePage.current = result;
      updateTemplate(
        lastTemplatePage.current[lastTemplatePage.current.length - 1]
      );
      setNeedSave(false);
      addToast({
        title: 'Template saved',
        styleType: 'success',
        dataCy: 'success-toast-title',
      });
    } catch (error) {
      addToast({
        title: 'Error on save template',
        description: formatUnknownErrorMessage(
          error,
          DefaultDescriptionBenefitEducation
        ),
        styleType: 'error',
        dataCy: 'error-toast-title',
      });
    }
  }, [
    editor,
    templateId,
    selectedTemplate,
    deleteEditTimeouts,
    setEditTemplateTimeout,
    syncTemplatePage,
    updateTemplate,
    addToast,
  ]);

  const handleSavePageTemplateAndCourse = useCallback(
    async (title: string, templateOptions: number) => {
      const courseIdNum = parseInt(courseId);
      const clientIdNum = parseInt(clientId);
      if (title === '') {
        addToast({
          title: 'Error on save template',
          description: 'Need a description.',
          styleType: 'error',
          dataCy: 'save-template-description-error-toast',
        });
        return;
      }
      if (!title || !editor || !clientIdNum || !courseIdNum) return;

      const htmlContent = editor.getHtml();
      const components = editor.getWrapper()?.toJSON() ?? '';
      const cssContent = editor.getCss({ json: false }) as string;
      const wrapperClasses: string[] =
        (editor.getWrapper()?.getClasses() as string[]) ?? [];
      const hasWrapper: boolean = wrapperClasses.includes(wrapperClass);

      try {
        await createTemplatePage({
          companyId: clientIdNum,
          courseId: courseIdNum,
          cssContent,
          hasWrapper,
          htmlContent,
          templateOptions,
          title,
          fonts: selectedCourse?.fonts ?? [],
          components: JSON.stringify(components),
        });
        handleSetNeedSave(false);
        addToast({
          title: 'Template saved',
          styleType: 'success',
          dataCy: 'success-toast-title',
        });
      } catch (error) {
        addToast({
          title: 'Error on save template',
          description: formatUnknownErrorMessage(
            error,
            DefaultDescriptionBenefitEducation
          ),
          styleType: 'error',
          dataCy: 'save-template-error-toast',
        });
      }
    },
    [
      addToast,
      clientId,
      courseId,
      editor,
      selectedCourse?.fonts,
      handleSetNeedSave,
    ]
  );

  const handleNewCourseSettings = useCallback(() => {
    handleSetNeedSave(true);
  }, [handleSetNeedSave]);

  const handleGlobalStyleChanged = useCallback(() => {
    setLoadingCoursePages(true);

    syncCoursePages(Number(courseId))
      .then((result: ICoursePage[]) => {
        lastCoursePages.current = result;
      })
      .catch((err: IApiThrowsError) => {
        Logger.error(err.message);
      })
      .finally(() => {
        setLoadingCoursePages(false);
      });
  }, [courseId, syncCoursePages]);

  const handleDiscardCourse = useCallback(
    (restoredCoursePages: ICoursePage[]) => {
      const selectedCoursePage = coursePages.at(selectedCoursePageIndex);

      let newIndex = 0;
      if (selectedCoursePage) {
        if (
          selectedCoursePage.id ===
          restoredCoursePages.at(selectedCoursePageIndex)?.id
        ) {
          newIndex = selectedCoursePageIndex;
        } else if (!(selectedCoursePage.isNew ?? false)) {
          const newIndexFounded = restoredCoursePages.findIndex(
            createFindById(selectedCoursePage)
          );

          newIndex = newIndexFounded < 0 ? 0 : newIndexFounded;
        }
      }

      const newSelectedCoursePage = restoredCoursePages.at(newIndex);
      if (newSelectedCoursePage) {
        const editorWrapperId = editor?.getWrapper()?.getId();
        if (editorWrapperId) {
          editor?.Css.remove(`#${editorWrapperId}`);
        }

        editor && updateEditorWithContent(editor, newSelectedCoursePage);
        selectCoursePageIndex(newIndex);
      }

      const newNeedSave = lastCoursePages.current.some(({ id }) => id < 0);

      coursePages.forEach(({ id, isNewFromTemplate = false }) => {
        if (!isNewFromTemplate || id <= 0) return;

        deleteCoursePage({ id }).catch((error: IApiThrowsError) => {
          addToast({
            title: 'Error on discard course page',
            description: formatUnknownErrorMessage(
              error,
              DefaultDescriptionBenefitEducation
            ),
            styleType: 'error',
            dataCy: 'discard-course-page-error-toast',
          });
        });
      });

      const {
        viewURL = null,
        passcodeProtected = false,
        passcode,
        status = 'In Progress',
      } = selectedCourse ?? {};

      const updatedSettings: ICourseSettingsProps = {
        viewUrl: viewURL?.trim() ?? null,
        status: status ?? 'In Progress',
        passcodeProtected,
        passcode: passcode ?? undefined,
      };

      updateCourseSettings(updatedSettings);
      setNeedSave(newNeedSave);
    },
    [
      addToast,
      coursePages,
      editor,
      selectCoursePageIndex,
      selectedCoursePageIndex,
      selectedCourse,
      updateCourseSettings,
    ]
  );

  const handleDiscardTemplate = useCallback(
    (newTemplatePages: ITemplatePage[]) => {
      const newSelectedTemplatePage = newTemplatePages.at(-1);
      if (newSelectedTemplatePage) {
        editor && updateEditorWithContent(editor, newSelectedTemplatePage);
        selectCoursePageIndex(-1);
      }
      handleSetNeedSave(false);
    },
    [editor, selectCoursePageIndex, handleSetNeedSave]
  );

  const chunkArray = (
    array: IBatchCoursePagesToPatch,
    chunkSize: number
  ): IBatchCoursePagesToPatch[] => {
    const chunks: IBatchCoursePagesToPatch[] = [];

    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
  };

  const sendToUpdate = useCallback(
    async (
      batchItems: IPatchCoursePagesProps | null
    ): Promise<ICoursePageDTO[] | never | null> => {
      try {
        if (!selectedClient) {
          throw new Error('Client not found');
        }

        await saveCourse();
        await saveTranslationSettings(selectedClient.id);
      } catch (error) {
        if (
          isAxiosError(error) &&
          error.response !== undefined &&
          error.response.status === 400
        ) {
          addToast({
            title: 'Error on update course',
            description: formatUnknownErrorMessage(
              error,
              DefaultDescriptionBenefitEducation
            ),
            styleType: 'error',
            dataCy: 'error-toast',
          });
          return null;
        }

        throw error;
      }

      if (batchItems) {
        const { courseId, pages } = batchItems;

        let updatedPages: ICoursePageDTO[] | null = null;

        if (pages.length <= COURSE_PAGES_BATCH_SIZE) {
          updatedPages = await patchCoursePages(batchItems);
        } else {
          // Create multiple batches of pages to send to the backend
          const pageChunks = chunkArray(pages, COURSE_PAGES_BATCH_SIZE);

          // Send each batch to the backend asynchronously
          const chunkPromises = pageChunks.map((chunk) =>
            patchCoursePages({ courseId, pages: chunk })
          );

          const chunkResults = await Promise.all(chunkPromises);

          updatedPages = chunkResults.flat();
        }

        await patchCoursePagesComponents({
          courseId,
        });

        await snapshotCourseVersionFromSavedState({ courseId });

        return updatedPages.sort(ByPageNumber);
      }

      return null;
    },
    [
      addToast,
      saveCourse,
      saveTranslationSettings,
      selectedClient,
      updateCourse,
    ]
  );

  const updateCoursePagesWithEditor = useCallback(
    (editor: Editor, index: number) => {
      const htmlContent = editor.getHtml();
      const cssContent = editor.getCss({ json: false }) as string;
      const wrapperClasses: string[] =
        (editor.getWrapper()?.getClasses() as string[]) ?? [];
      const hasWrapper: boolean = wrapperClasses.includes(wrapperClass);

      const components = JSON.stringify(editor.getWrapper()?.toJSON() ?? '');

      const newCoursePages = changeCoursePageContent(index, {
        cssContent,
        htmlContent,
        hasWrapper,
        components,
      });

      if (!needSave) {
        const oldCoursePage = lastCoursePages.current.at(index);
        const newCoursePage = newCoursePages.at(index);

        if (!oldCoursePage || !newCoursePage) {
          handleSetNeedSave(true);
        } else {
          const needToBeSaved = checkContentChanged(
            newCoursePage,
            oldCoursePage
          );
          handleSetNeedSave(needToBeSaved);
        }
      }

      return { newCoursePages, index };
    },
    [changeCoursePageContent, needSave, handleSetNeedSave]
  );

  const combinePages = (pages: ICoursePage[], updatedPages: ICoursePage[]) => {
    return pages.map((page) => {
      const updatedPage = updatedPages.find(
        (p) => p.pageNumber === page.pageNumber
      );
      return updatedPage ?? page;
    });
  };

  const updatePageNumberValues = (pages: ICoursePage[]) => {
    return pages.map((page, index) => {
      return { ...page, pageNumber: index + 1 };
    });
  };

  const handleSaveModifications = useCallback(
    async (pagesToUpdate?: ICoursePage[]) => {
      const courseIdFromPath = Number(courseId);
      if (!isAllowedToEdit || !courseIdFromPath) {
        return;
      }
      setSavingCourse(true);

      const htmlContent = editor?.getHtml() ?? '';
      const components = editor?.getWrapper()?.toJSON() ?? '';
      const cssContent = editor?.getCss({ json: false }) ?? '';

      const wrapperClasses: string[] =
        (editor?.getWrapper()?.getClasses() as string[]) ?? [];
      const hasWrapper: boolean = wrapperClasses.includes(wrapperClass) ?? true;

      const newCoursePages =
        selectedCoursePageIndex >= 0
          ? produce(coursePages, (draft) => {
              draft[selectedCoursePageIndex].hasWrapper = hasWrapper;
              draft[selectedCoursePageIndex].htmlContent = htmlContent;
              draft[selectedCoursePageIndex].cssContent = cssContent;
              draft[selectedCoursePageIndex].edited = true;
              draft[selectedCoursePageIndex].components =
                JSON.stringify(components);
            })
          : coursePages;

      const batchItems = updateCoursePages({
        courseId: courseIdFromPath,
        coursePages: newCoursePages,
        updatedPages: pagesToUpdate ?? coursePages,
        lastCoursePages: [...lastCoursePages.current],
      });
      const currentCoursePage = newCoursePages.at(selectedCoursePageIndex);

      try {
        deleteEditTimeouts();

        const updatedPages = await sendToUpdate(batchItems);

        if (updatedPages) {
          setEditCourseTimeout();

          const pages = combinePages(
            updatePageNumberValues(coursePages),
            updatedPages
          );

          overwriteCoursePages(pages);
          lastCoursePages.current = pages;

          if (currentCoursePage) {
            // handle to persist the same page selected
            const isNewPage = currentCoursePage.id <= 0;

            const newSelectedCoursePageIndex = pages.findIndex(
              isNewPage
                ? createFindByContent(currentCoursePage)
                : createFindById(currentCoursePage)
            );

            const page = pages[newSelectedCoursePageIndex];

            if (editor) {
              if (newSelectedCoursePageIndex >= 0) {
                if (
                  newSelectedCoursePageIndex !== selectedCoursePageIndex ||
                  updatedPages
                ) {
                  selectCoursePageIndex(newSelectedCoursePageIndex);
                  updateEditorWithContent(editor, page);
                }
              } else if (pages.length) {
                selectCoursePageIndex(0);
                updateEditorWithContent(editor, pages[0]);
              }
            }
          }
          setNeedSave(false);
        } else {
          setNeedSave(false);
        }
      } catch (error) {
        addToast({
          title: 'Unable to save changes',
          styleType: 'error',
          description: formatUnknownErrorMessage(
            error,
            DefaultDescriptionBenefitEducation
          ),
        });
      } finally {
        setSavingCourse(false);
      }
    },
    [
      addToast,
      courseId,
      coursePages,
      deleteEditTimeouts,
      editor,
      isAllowedToEdit,
      overwriteCoursePages,
      selectCoursePageIndex,
      selectedCoursePageIndex,
      sendToUpdate,
      setEditCourseTimeout,
    ]
  );

  const handleSaveModificationsCourseColors = useCallback(
    async (pages: ICoursePage[]) => {
      await handleSaveModifications(pages);
    },
    [handleSaveModifications]
  );

  const handleChangePagesOrder = useCallback(() => {
    setNeedSave(true);
  }, [setNeedSave]);

  const handleSelectPage = useCallback(
    (index: number, oldIndex: number, updateEditor = true) => {
      if (editor) {
        updateCoursePagesWithEditor(editor, oldIndex);
        const page = coursePages.at(index);
        updateEditor &&
          updateEditorWithContent(
            editor,
            page ?? { cssContent: '', htmlContent: '' },
            startIgnorePeriod
          );
      }
    },
    [coursePages, editor, updateCoursePagesWithEditor, startIgnorePeriod]
  );

  const handleRenamePage = useCallback(() => {
    setNeedSave(true);
  }, [setNeedSave]);

  const handleRestorePage = useCallback(
    (index: number, historyPage: IHistoryPage) => {
      setNeedSave(true);

      if (index === selectedCoursePageIndex) {
        editor &&
          updateEditorWithContent(
            editor,
            {
              htmlContent: historyPage.page.htmlContent ?? '',
              cssContent: historyPage.page.cssContent ?? '',
              components: historyPage.page.components ?? '{}',
              hasWrapper: historyPage.page.hasWrapper ?? true,
            },
            startIgnorePeriod
          );
      }
    },
    [editor, selectedCoursePageIndex, setNeedSave, startIgnorePeriod]
  );

  const handleDeletePage = useCallback(
    ({ indexToSelected, idRemoved, newCoursePages }: IDeleteCoursePageInfo) => {
      if (indexToSelected !== undefined && editor) {
        const page = newCoursePages?.at(indexToSelected);
        updateEditorWithContent(
          editor,
          page ?? { cssContent: '', htmlContent: '' },
          startIgnorePeriod
        );
      }

      if (idRemoved > 0) {
        const page = {
          ...lastCoursePages.current.find(createFindById({ id: idRemoved })),
        };

        if (page) {
          page.id = generateNegativeTimestamp();
          setNeedSave(true);
        }
      }

      setIsDropdownOpen(false);
    },
    [editor, startIgnorePeriod, setNeedSave]
  );

  const handleDestroyedEditor = useCallback(() => {
    setEditor(null);
  }, []);

  const handleChangedFontsToEditor = useCallback(
    (newFonts: IFontDTO[]) => {
      if (editor) {
        const styleManager = editor.StyleManager;
        const fontFamily = styleManager.getBuiltIn('font-family');
        const head = editor.Canvas.getDocument().head;

        // @ts-expect-error: the options is missing in PropertyProps
        let customFontsCount = fontFamily?.options.length - defaultFontsCount;
        while (customFontsCount--) {
          // @ts-expect-error: the options is missing in PropertyProps
          fontFamily?.options.pop();
        }

        newFonts.forEach((font: IFontDTO) => {
          // @ts-expect-error: the options is missing in PropertyProps
          fontFamily?.options.push({
            label: `${font.family}`,
            id: `"${font.family}", ${font.category}`,
          } as ICustomFont);
          head.insertAdjacentHTML(
            'beforeend',
            `<link href="${GOOGLE_FONTS_API_URL}?family=${font.family}" rel="stylesheet">`
          );
        });

        if (fontFamily) {
          styleManager.addBuiltIn('font-family', fontFamily);
        }

        styleManager.render();
      }
    },
    [defaultFontsCount, editor]
  );

  const loadBackground = useCallback(() => {
    if (
      editor &&
      (Boolean(coursePages.length) || Boolean(templatePages.length))
    ) {
      const openedPageCourse = coursePages.at(
        selectedCoursePageIndex >= 0 ? selectedCoursePageIndex : 0
      );
      const openedPageTemplate = templatePages[templatePages.length - 1];
      const openedPage = openedPageCourse ?? openedPageTemplate;

      const components = openedPage?.components;
      if (components) {
        const pageComponentsJSON = JSON.parse(components) as {
          attributes: { id: string };
        };
        const body = editor.getWrapper();
        body?.setAttributes({ id: pageComponentsJSON.attributes?.id ?? '' });
      }
    }
  }, [coursePages, editor, selectedCoursePageIndex, templatePages]);

  useEffect(() => {
    const css = editor?.Css;

    if (css) {
      const globalStyle = selectedCourse?.globalStyle;

      if (globalStyle) {
        css.setRule('body', {
          color: globalStyle.paragraphFontColor,
          'font-size': `${String(globalStyle.paragraphFontSize)}${String(
            globalStyle.paragraphFontSizeUnit
          )}`,
        } as CSS.StandardPropertiesHyphen);

        css.setRule('.heading', {
          color: globalStyle.headlineFontColor,
          'font-size': `${String(globalStyle.headlineFontSize)}${String(
            globalStyle.headlineFontSizeUnit
          )}`,
        } as CSS.StandardPropertiesHyphen);

        if (globalStyle?.paragraphFont?.family) {
          css.setRule('body', {
            'font-family':
              globalStyle.paragraphFont.family +
              ', ' +
              globalStyle.paragraphFont.category,
            color: globalStyle.paragraphFontColor,
            'font-size': `${String(globalStyle.paragraphFontSize)}${String(
              globalStyle.paragraphFontSizeUnit
            )}`,
          } as CSS.StandardPropertiesHyphen);
        }

        if (globalStyle?.headlineFont?.family) {
          css.setRule('.heading', {
            'font-family':
              globalStyle.headlineFont.family +
              ', ' +
              globalStyle.headlineFont.category,
            color: globalStyle.headlineFontColor,
            'font-size': `${String(globalStyle.headlineFontSize)}${String(
              globalStyle.headlineFontSizeUnit
            )}`,
          } as CSS.StandardPropertiesHyphen);
        }

        if (globalStyle?.buttonFontColor && globalStyle?.buttonColor) {
          css.setRule('.mailto-link', {
            'background-color': globalStyle.buttonColor,
            border: '1px solid ' + globalStyle.buttonColor,
            color: globalStyle.buttonFontColor,
            'border-radius': '15px',
            display: 'inline-block',
            padding: '10px 25px',
            'text-decoration': 'none',
          } as CSS.StandardPropertiesHyphen);

          css.setRule('.link-button', {
            'background-color': globalStyle.buttonColor,
            border: '1px solid ' + globalStyle.buttonColor,
            color: globalStyle.buttonFontColor,
            'border-radius': '15px',
            display: 'inline-block',
            padding: '10px 25px',
            'text-decoration': 'none',
          } as CSS.StandardPropertiesHyphen);

          css.setRule('.link-document', {
            'background-color': globalStyle.buttonColor,
            border: '1px solid ' + globalStyle.buttonColor,
            color: globalStyle.buttonFontColor,
            'border-radius': '15px',
            display: 'inline-block',
            padding: '10px 25px',
            'text-decoration': 'none',
          } as CSS.StandardPropertiesHyphen);

          css.setRule('.outline-button', {
            'background-color': 'transparent',
            color: globalStyle.buttonColor,
            'border-width': '2px',
          } as CSS.StandardPropertiesHyphen);

          css.setRule('.outline-button:hover', {
            'background-color': 'transparent',
          } as CSS.StandardPropertiesHyphen);

          css.setRule('.mailto-link:hover', {
            'background-color': globalStyle.buttonColor,
            opacity: '0.9',
          } as CSS.StandardPropertiesHyphen);

          css.setRule('.link-button:hover', {
            'background-color': globalStyle.buttonColor,
            opacity: '0.9',
          } as CSS.StandardPropertiesHyphen);

          css.setRule('.link-document:hover', {
            'background-color': globalStyle.buttonColor,
            opacity: '0.9',
          } as CSS.StandardPropertiesHyphen);

          const primaryColor = globalStyle.primaryColor
            ? globalStyle.primaryColor
            : globalStyle.buttonColor; // is not globalStyleDefault.primaryColor because of old courses

          css.setRule('.primary-cl', {
            color: primaryColor,
          } as CSS.StandardPropertiesHyphen);

          css.setRule('.primary-bg', {
            'background-color': primaryColor,
          } as CSS.StandardPropertiesHyphen);

          css.setRule('.secondary-cl', {
            color:
              globalStyle.secondaryColor ?? globalStyleDefault.secondaryColor,
          } as CSS.StandardPropertiesHyphen);

          css.setRule('.secondary-bg', {
            'background-color':
              globalStyle.secondaryColor ?? globalStyleDefault.secondaryColor,
          } as CSS.StandardPropertiesHyphen);

          css.setRule('.color-filter', {
            filter: `hue-rotate(${
              globalStyle.hueRotate ?? globalStyleDefault.hueRotate
            }deg)`,
          } as CSS.StandardPropertiesHyphen);
        }

        if (globalStyle?.pageWidth !== null) {
          const pageWidthAux =
            String(globalStyle.pageWidth) + (globalStyle.pageWidthUnit ?? '');
          css.setRule('.content-center', {
            padding: `0 calc((100% - ${pageWidthAux})/2)`,
          } as CSS.StandardPropertiesHyphen);
        }
      }
      loadBackground();
    }
  }, [
    selectedCourse?.globalStyle,
    editor,
    selectedCoursePageIndex,
    loadBackground,
  ]);

  const loadingInitial =
    loadingSelectedClient || loadingCourse || loadingCoursePages;

  const handleAddPage = useCallback(
    (newPage: ICoursePage, newSelectPosition: number) => {
      if (editor) {
        const hadPageSelected = newSelectPosition > 0;
        if (hadPageSelected && newSelectPosition !== selectedCoursePageIndex) {
          const selectedIndexAfterAdded =
            newSelectPosition < selectedCoursePageIndex
              ? selectedCoursePageIndex + 1
              : selectedCoursePageIndex;
          updateCoursePagesWithEditor(editor, selectedIndexAfterAdded);
        }
        updateEditorWithContent(editor, newPage, startIgnorePeriod);
      }
      selectCoursePageIndex(newSelectPosition);
      setNeedSave(true);

      if (newPage.id > 0) {
        selectCourseByID(Number(courseId))
          .then((syncedCourse) => {
            handleChangedFontsToEditor(syncedCourse?.fonts ?? []);
          })
          .catch((err) => {
            Logger.error('error: ', err);
            addToast({
              title: 'Error while synchronizing the Course',
              description: formatUnknownErrorMessage(
                err,
                DefaultDescriptionBenefitEducation
              ),
              styleType: 'error',
              dataCy: 'sync-course-error-toast',
            });
          });
      }
    },
    [
      addToast,
      courseId,
      editor,
      handleChangedFontsToEditor,
      selectCourseByID,
      selectCoursePageIndex,
      selectedCoursePageIndex,
      updateCoursePagesWithEditor,
      startIgnorePeriod,
      setNeedSave,
    ]
  );

  const handleDeleteMultiplePages = useCallback(
    async (ids: number[]) => {
      if (!ids.length) return;
      if (!editor) return;

      try {
        const tmpCoursePages = updateCoursePagesWithEditor(
          editor,
          selectedCoursePageIndex
        );

        const pageSelected = tmpCoursePages.newCoursePages.at(
          selectedCoursePageIndex
        );

        const { newCoursePages } = await deleteMultipleCoursePages(
          ids,
          Number(courseId),
          tmpCoursePages.newCoursePages
        );

        let newSelectedCoursePageIndex = 0;
        // if pageSelected is deleted, select the first page
        if (pageSelected) {
          if (newCoursePages.findIndex(createFindById(pageSelected)) < 0) {
            if (selectedCoursePageIndex >= newCoursePages.length) {
              newSelectedCoursePageIndex = newCoursePages.length - 1;
            } else {
              newSelectedCoursePageIndex = selectedCoursePageIndex;
            }
          } else {
            newSelectedCoursePageIndex = newCoursePages.findIndex(
              createFindById(pageSelected)
            );
          }
        }

        const page = newCoursePages.at(newSelectedCoursePageIndex);

        if (!page) return;
        selectCoursePageIndex(newSelectedCoursePageIndex);
        updateEditorWithContent(editor, page);

        addToast({
          title: 'Pages deleted',
          styleType: 'success',
          dataCy: 'delete-pages-success-toast',
        });
      } catch (error) {
        addToast({
          title: 'Unable to delete the pages',
          styleType: 'error',
          description: formatUnknownErrorMessage(
            error,
            DefaultDescriptionBenefitEducation
          ),
        });
      }
    },
    [
      addToast,
      courseId,
      deleteMultipleCoursePages,
      editor,
      selectCoursePageIndex,
      selectedCoursePageIndex,
      updateCoursePagesWithEditor,
    ]
  );

  return (
    <Container data-cy="page-container">
      <FullscreenLoader notLoading={!savingCourse}></FullscreenLoader>
      <SettingCourseHeader
        isCreateCourse={Boolean(courseId)}
        isAllowedToEdit={
          !isLockedByOtherUser && (isAllowedToEdit || !savingCourse)
        }
        isUserManager={isUserManager}
        isUserAdmin={isUserAdmin}
        isClientFullCourseEditor={isClientFullCourseEditor}
        isDropdownOpen={isDropdownOpen}
        lastCoursePages={lastCoursePages}
        lastTemplatePage={lastTemplatePage}
        loading={loadingInitial}
        needSave={needSave}
        onChangeTitle={handleChangeTitle}
        onCourseFontsChange={handleChangedFontsToEditor}
        onDiscardCourse={handleDiscardCourse}
        onDiscardTemplate={handleDiscardTemplate}
        onSaveModifications={handleSaveModifications}
        onSavePageTemplate={handleSavePageTemplateAndCourse} // using when save template and courses
        onUpdatePageTemplate={handleUpdateTemplate} // using when Edit Course is opened from the template list screen
        onSettingsChange={handleNewCourseSettings}
        onSaveGlobalStyle={handleGlobalStyleChanged}
        onBackButtonAdditionalAction={deleteEditTimeouts}
      />
      <MainContainer data-cy="page-main-container">
        {courseId && (
          <CoursePages
            disabled={savingCourse || loadingEditor || loadingInitial}
            isAllowedToEdit={
              (isClientFullCourseEditor || isUserManager || isUserAdmin) &&
              isAllowedToEdit
            }
            isAllowedToAddPage={
              (isUserManager || isUserAdmin) && isAllowedToEdit
            }
            isClientFullCourseEditor={isClientFullCourseEditor}
            isDropdownOpen={isDropdownOpen}
            loading={loadingInitial}
            onAddPage={handleAddPage}
            onChangePagesOrder={handleChangePagesOrder}
            onDeletePage={handleDeletePage}
            onDropdownOpenChange={setIsDropdownOpen}
            onRenamePage={handleRenamePage}
            onRestorePage={handleRestorePage}
            onSelectPage={handleSelectPage}
            onSaveModifications={handleSaveModificationsCourseColors}
            onDeletedMultiplePages={handleDeleteMultiplePages}
          />
        )}
        <MainContent data-cy="page-main-content">
          {loadingInitial ? (
            <LoadingCoursePagesContainer data-cy="loading-course-pages-container">
              <LoadingCoursePages dataCy="loading-course-pages" />
            </LoadingCoursePagesContainer>
          ) : (courseId && coursePages.length) ||
            (templateId && selectedTemplate) ? (
            <WebBuilder
              customConfig={customConfig}
              isDraggingOver={isDragging}
              onEditorReady={handleSetEditor}
              onEditorDestroyed={handleDestroyedEditor}
              fonts={selectedCourse?.fonts ?? []}
              setDefaultFontsCount={setDefaultFontsCount}
            />
          ) : (
            <NoPagesContainer data-cy="noPages-container">
              <NoPagesText data-cy="text-noPage-edit">
                No page to edit
              </NoPagesText>
            </NoPagesContainer>
          )}
        </MainContent>
      </MainContainer>
    </Container>
  );
};
